import JFileContainer from '@/models/JFileContainer'
import { db, storage, UploadMetadata } from '@/firebase'
import {
  id,
  ImageInterface,
  AvatarDocInterface,
  ExifInterface,
} from '@/types/models'
import Image from '@/models/Image'
import firebaseConfig from '@root/firebase-config'

import EXIF from 'exif-js'
import { parse, isValid as isValidDate } from 'date-fns'
import { readImageFile } from '@/helpers/fileHelpers'
import { StorageReference, UploadTaskSnapshot } from 'firebase/storage'
import { DocumentData, DocumentReference, SetOptions } from 'firebase/firestore'

const BUCKET_NAME = firebaseConfig.storageBucket
interface UploadCallbacks {
  progress?: (s: UploadTaskSnapshot) => void
  error?: (e: Error) => void
  success?: (s: UploadTaskSnapshot) => void
}

export const DEFAULT_METADATA: UploadMetadata = {
  cacheControl: 'private, max-age=604800, immutable',
}

const getTokenFromDownloadUrl = (downloadUrl: string) =>
  new URL(downloadUrl).searchParams.get('token')

const getExif = (jfile: JFileContainer): Promise<ExifInterface> => {
  return new Promise((resolve, reject) => {
    jfile.file
      .arrayBuffer() // get ArrayBuffer for EXIF
      .then((arrBuffer) => resolve(EXIF.readFromBinaryFile(arrBuffer)))
      .catch((err) => reject(err))
  })
}

const setFirestoreDoc = (
  firestoreRef: DocumentReference,
  docData: DocumentData,
  setOptions: SetOptions = {}
) => db.setDoc(firestoreRef, docData, setOptions)

// const updateFirestoreDoc = <AppModelType = DocumentData, DbModelType extends DocumentData = DocumentData>(
//   firestoreRef: DocumentReference<AppModelType, DbModelType>,
//   updates: UpdateData<DbModelType>
// ) => {
//   if (updates === undefined) return Promise.reject("no updates provided");
//   return db.updateDoc(firestoreRef, updates);
// };

const uploadFileToStorage = (
  jfile: JFileContainer,
  storageRef: StorageReference,
  metadata: UploadMetadata = DEFAULT_METADATA,
  callbacks?: UploadCallbacks
): Promise<JFileContainer> => {
  return new Promise((resolve, reject) => {
    // set uploading status
    jfile.uploading = true

    jfile.path = storageRef.fullPath
    jfile.metadata = metadata

    // Upload file and metadata to the object 'images/mountains.jpg'
    const uploadTask = storage.uploadBytesResumable(
      storageRef,
      jfile.file,
      metadata
    )

    // Listen for state changes, errors, and completion of the upload.
    uploadTask.on(
      'state_changed',
      // TaskEvent.STATE_CHANGED, // or 'state_changed'
      (snapshot: UploadTaskSnapshot) => {
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        // update progress
        jfile.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        jfile.state = snapshot.state

        if (callbacks?.progress) callbacks.progress(snapshot)
      },
      (err: Error) => {
        jfile.error = err

        if (callbacks?.error) callbacks.error(err)

        reject(err)
      },
      () => {
        storage
          .getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL: string) => {
            jfile.url = downloadURL
            jfile.uploading = false
            jfile.state = 'success'
          })
          .then(() => {
            if (callbacks?.success) callbacks.success(uploadTask.snapshot)
          })
          .then(() => resolve(jfile))
          .catch((err: Error) => reject(err))
      }
    )
  })
}

export const deleteImage = (gardenId: id, imageId: id): Promise<void> =>
  new Promise((resolve, reject) => {
    const docRef = db.doc('gardens', gardenId, 'images', imageId)

    db.getDoc(docRef.withConverter(Image.firestoreConverter(gardenId)))
      .then((doc) => {
        if (!doc.exists) {
          throw new Error(`image doc does not exist: ${imageId}`)
        }

        return doc.data() as Image
      })
      .then((image: Image) => {
        const storageActions = Object.values(image.fullpaths).map(
          (path: string) => storage.delete(storage.ref(path))
        )

        return Promise.all([...storageActions, db.deleteDoc(docRef)])
      })
      .then(() => resolve())
      .catch((err: Error) => reject(err))
  })

export const uploadFileToFirestore = (
  jfile: JFileContainer,
  firestoreRef: DocumentReference,
  docData: DocumentData,
  storageRef: StorageReference,
  metadata: UploadMetadata = DEFAULT_METADATA,
  storageCallbacks?: UploadCallbacks
): Promise<JFileContainer> =>
  new Promise((resolve, reject) => {
    uploadFileToStorage(jfile, storageRef, metadata, storageCallbacks)
      .then((jf: JFileContainer) => {
        if (!jf.url) throw Error('url not found')
        docData.fullpath = storageRef.fullPath

        docData.bucket = BUCKET_NAME
        docData.token = getTokenFromDownloadUrl(jf.url)

        return Promise.all([jf, setFirestoreDoc(firestoreRef, docData)])
      })
      .then(([jf]) => resolve(jf))
      .catch((err: Error) => reject(err))
  })

export const uploadImageToFirestoreRef = async (
  jfile: JFileContainer,
  newDocRef: DocumentReference,
  newDocData: ImageInterface,
  storageRef: StorageReference,
  metadata: UploadMetadata = DEFAULT_METADATA
): Promise<JFileContainer> => {
  const exifData = await getExif(jfile)

  if (exifData) {
    const takenAt = parse(
      exifData.DateTimeOriginal as string,
      'yyyy:MM:dd HH:mm:ss',
      new Date()
    )
    if (takenAt && isValidDate(takenAt)) newDocData.takenAt = takenAt

    newDocData.exif = JSON.parse(JSON.stringify(exifData))
  }

  const img = await readImageFile(jfile.file)

  // console.log(img)

  newDocData.width = img.naturalWidth
  newDocData.height = img.naturalHeight

  return uploadFileToFirestore(
    jfile,
    newDocRef,
    newDocData,
    storageRef,
    metadata
  )
}

export const uploadImageToFirestore = async (
  jfile: JFileContainer,
  gardenId: id,
  refs: Array<string>,
  note?: string
): Promise<JFileContainer> => {
  // get new doc for image
  const newDocRef = db.newDoc('gardens', gardenId, 'images')
  const newDocData = {
    isNew: true,
    note: note || null,
    refs,
  } as ImageInterface

  return uploadImageToFirestoreRef(
    jfile,
    newDocRef.withConverter(Image.firestoreConverter(gardenId)),
    newDocData,
    storage.ref(`gardens/${gardenId}/images/${newDocRef.id}`),
    DEFAULT_METADATA
  )
}

export const uploadAvatar = async (
  jfile: JFileContainer,
  storageRef: StorageReference,
  firestoreRef: DocumentReference,
  docProp = 'avatar',
  metadata: UploadMetadata = DEFAULT_METADATA
): Promise<JFileContainer> => {
  const newDocData = {} as AvatarDocInterface

  const exifData = await getExif(jfile)

  if (exifData) {
    const takenAt = parse(
      ((exifData.DateTimeOriginal || '') as string).trim(),
      'yyyy:MM:dd HH:mm:ss',
      new Date()
    )
    if (takenAt && isValidDate(takenAt)) newDocData.takenAt = takenAt

    newDocData.exif = JSON.parse(JSON.stringify(exifData))
  }

  const img = await readImageFile(jfile.file)

  // console.log(img)

  newDocData.width = img.naturalWidth
  newDocData.height = img.naturalHeight

  return new Promise((resolve, reject) => {
    uploadFileToStorage(jfile, storageRef, metadata)
      .then((jf: JFileContainer) => {
        if (!jf.url) throw Error('url not found')
        newDocData.fullpath = storageRef.fullPath

        newDocData.bucket = BUCKET_NAME
        newDocData.token = getTokenFromDownloadUrl(jf.url)

        return Promise.all([
          jf,
          // updateFirestoreDoc(firestoreRef, { [docProp]: newDocData }),
          db.updateDoc(firestoreRef, { [docProp]: newDocData }),
        ])
      })
      .then(([jf]) => resolve(jf))
      .catch((err: Error) => reject(err))
  })
}

export const uploadFile = (
  jfile: JFileContainer,
  path: (f: File) => string,
  metadata: UploadMetadata = DEFAULT_METADATA,
  callbacks?: UploadCallbacks
): Promise<JFileContainer> =>
  uploadFileToStorage(jfile, storage.ref(path(jfile.file)), metadata, callbacks)
